import ListingFilterDefinition, {
  ListingFilterDefinitionOption,
  ListingFilterDefinitionOptionBadgeData,
  ListingFilterDefinitionOptionSelectData,
  ListingFilterDefinitionOptionSelectDataType,
} from "../../../../../common/components/listing/filter/types/listing-filter-definition";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import OrderOptimizerTaxiListingFilter, {
  OrderOptimizerTaxiListingIgnoreDriverRoutesFilter,
  OrderOptimizerTaxiListingOrderHumanIdFilter,
  OrderOptimizerTaxiListingMaximumDelayFilter,
  OrderOptimizerTaxiListingMaximumHaltingTimeFilter,
  OrderOptimizerTaxiListingStatusFilterValue,
  OrderOptimizerTaxiListingStatusFilter,
} from "../common/types/order-optimizer-taxi-listing-filter";
import OrderOptimizerTaxiListingFilterType from "../common/types/order-optimizer-taxi-listing-filter-type";
import { OrderOptimizerTaxiListingItemStatus } from "../common/types/order-optimizer-taxi-listing-item";
import orderOptimizerTaxiListingTableRowHelper from "../table/row/order-optimizer-taxi-listing-table-row.helper";

const orderOptimizerTaxiListingFiltersHelper = () => {
  const translations =
    orderOptimizerTranslationsHelper.getTaxiListingTranslations();

  const getStatusLabel = orderOptimizerTaxiListingTableRowHelper.getStatusLabel;

  const createBadgeData = (
    value: string,
    textTemplate: string,
    titleTemplate: string,
  ): ListingFilterDefinitionOptionBadgeData => {
    return {
      text: textTemplate.replace("#{query}", value),
      title: titleTemplate.replace("#{query}", value),
    };
  };

  const getSelectDataForOrderHumanId = (
    query: string,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.NUMERIC_QUERY,
      options: [
        {
          label: translations.filters.search.orderHumanIdTemplateLabel.replace(
            "#{query}",
            query,
          ),
          value: Number.parseInt(query),
        },
      ],
    };
  };

  const getSelectDataForMaximumDelay = (
    openMaximumDelayModal: () => void,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.CUSTOM,
      option: {
        label: translations.filters.search.maximumDelayTemplateLabel,
        onClick: () => openMaximumDelayModal(),
      },
    };
  };

  const getSelectDataForMaximumHaltingTime = (
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOptionSelectData => {
    return {
      type: ListingFilterDefinitionOptionSelectDataType.CUSTOM,
      option: {
        label: translations.filters.search.maximumHaltingTimeTemplateLabel,
        onClick: () => openMaximumHaltingTimeModal(),
      },
    };
  };

  const getSelectDataForIgnoreDriverRoutes =
    (): ListingFilterDefinitionOptionSelectData => {
      return {
        type: ListingFilterDefinitionOptionSelectDataType.DEFAULT,
        options: [
          {
            label: translations.filters.search.ignoreDriverRoutesTemplateLabel,
            value: true,
          },
        ],
      };
    };

  const getSelectDataForStatus = (
    status: OrderOptimizerTaxiListingItemStatus,
  ): {
    label: string;
    value: OrderOptimizerTaxiListingStatusFilterValue;
  }[] => {
    return [
      {
        label: translations.filters.search.statusTemplateLabel.replace(
          "#{query}",
          getStatusLabel(status),
        ),
        value: {
          status: status,
          exclude: false,
        },
      },
      {
        label: translations.filters.search.excludeStatusTemplateLabel.replace(
          "#{query}",
          getStatusLabel(status),
        ),
        value: {
          status: status,
          exclude: true,
        },
      },
    ];
  };

  const getSelectDataForStatuses =
    (): ListingFilterDefinitionOptionSelectData => {
      return {
        type: ListingFilterDefinitionOptionSelectDataType.DEFAULT,
        options: Object.values(OrderOptimizerTaxiListingItemStatus)
          .map(getSelectDataForStatus)
          .flat(),
      };
    };

  const getSelectDataByFilterType = (
    type: OrderOptimizerTaxiListingFilterType,
    query: string,
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOptionSelectData => {
    const options: Record<
      OrderOptimizerTaxiListingFilterType,
      (query: string) => ListingFilterDefinitionOptionSelectData
    > = {
      [OrderOptimizerTaxiListingFilterType.ORDER_HUMAN_ID]:
        getSelectDataForOrderHumanId,
      [OrderOptimizerTaxiListingFilterType.MAXIMUM_DELAY]: () =>
        getSelectDataForMaximumDelay(openMaximumDelayModal),
      [OrderOptimizerTaxiListingFilterType.MAXIMUM_HALTING_TIME]: () =>
        getSelectDataForMaximumHaltingTime(openMaximumHaltingTimeModal),
      [OrderOptimizerTaxiListingFilterType.IGNORE_DRIVER_ROUTES]:
        getSelectDataForIgnoreDriverRoutes,
      [OrderOptimizerTaxiListingFilterType.STATUS]: getSelectDataForStatuses,
    };

    return options[type](query);
  };

  const getBadgeForOrderHumanId = (
    filterValue: OrderOptimizerTaxiListingOrderHumanIdFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.orderHumanIdLabel,
      translations.filters.orderHumanIdTitle,
    );
  };

  const getBadgeForMaximumDelay = (
    filterValue: OrderOptimizerTaxiListingMaximumDelayFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.maximumDelayLabel,
      translations.filters.maximumDelayTitle,
    );
  };

  const getBadgeForMaximumHaltingTime = (
    filterValue: OrderOptimizerTaxiListingMaximumHaltingTimeFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.maximumHaltingTimeLabel,
      translations.filters.maximumHaltingTimeTitle,
    );
  };

  const getBadgeForIgnoreDriverRoutes = (
    filterValue: OrderOptimizerTaxiListingIgnoreDriverRoutesFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    return createBadgeData(
      String(filterValue),
      translations.filters.ignoreDriverRoutesLabel,
      translations.filters.ignoreDriverRoutesTitle,
    );
  };

  const getBadgeDataForStatus = (
    filterValue: OrderOptimizerTaxiListingStatusFilter["value"],
  ) => {
    if (filterValue.exclude) {
      return createBadgeData(
        getStatusLabel(filterValue.status),
        translations.filters.excludeStatusLabel,
        translations.filters.excludeStatusTtitle,
      );
    } else {
      return createBadgeData(
        getStatusLabel(filterValue.status),
        translations.filters.statusLabel,
        translations.filters.statusTitle,
      );
    }
  };

  const getBadgeDataByFilterType = (
    type: OrderOptimizerTaxiListingFilterType,
    filterValue: OrderOptimizerTaxiListingFilter["value"],
  ): ListingFilterDefinitionOptionBadgeData => {
    const options: Record<
      OrderOptimizerTaxiListingFilterType,
      (
        filterValue: OrderOptimizerTaxiListingFilter["value"],
      ) => ListingFilterDefinitionOptionBadgeData
    > = {
      [OrderOptimizerTaxiListingFilterType.ORDER_HUMAN_ID]:
        getBadgeForOrderHumanId,
      [OrderOptimizerTaxiListingFilterType.MAXIMUM_DELAY]:
        getBadgeForMaximumDelay,
      [OrderOptimizerTaxiListingFilterType.MAXIMUM_HALTING_TIME]:
        getBadgeForMaximumHaltingTime,
      [OrderOptimizerTaxiListingFilterType.IGNORE_DRIVER_ROUTES]:
        getBadgeForIgnoreDriverRoutes,
      [OrderOptimizerTaxiListingFilterType.STATUS]: getBadgeDataForStatus,
    };

    return options[type](filterValue);
  };

  const getFilterDefinitionOptionByType = (
    type: OrderOptimizerTaxiListingFilterType,
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOption<OrderOptimizerTaxiListingFilter> => {
    return {
      filterType: type,
      getSelectData: (query) =>
        getSelectDataByFilterType(
          type,
          query,
          openMaximumDelayModal,
          openMaximumHaltingTimeModal,
        ),
      getBadgeData: (value) => getBadgeDataByFilterType(type, value),
    };
  };

  const getFilterDefinitionOptions = (
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinitionOption<OrderOptimizerTaxiListingFilter>[] => {
    return Object.values(OrderOptimizerTaxiListingFilterType).map((type) =>
      getFilterDefinitionOptionByType(
        type,
        openMaximumDelayModal,
        openMaximumHaltingTimeModal,
      ),
    );
  };

  const getFilterDefinition = (
    openMaximumDelayModal: () => void,
    openMaximumHaltingTimeModal: () => void,
  ): ListingFilterDefinition<OrderOptimizerTaxiListingFilter> => {
    return {
      options: getFilterDefinitionOptions(
        openMaximumDelayModal,
        openMaximumHaltingTimeModal,
      ),
    };
  };

  const STATUS_FILTER_EXCLUSION_PREFIX = "not-";

  const getStatusRouteQueryParamFromValue = (
    value: OrderOptimizerTaxiListingStatusFilterValue,
  ): string => {
    return `${value.exclude ? STATUS_FILTER_EXCLUSION_PREFIX : ""}${value.status.toLocaleLowerCase()}`;
  };

  const getStatusValueFromRouteQueryParam = (
    routeQueryParam: string,
  ): OrderOptimizerTaxiListingStatusFilterValue | undefined => {
    const checkRouteParam = (param: string): boolean => {
      return Object.values(OrderOptimizerTaxiListingItemStatus)
        .map((status) => status.toLocaleLowerCase())
        .includes(param);
    };

    if (routeQueryParam.startsWith(STATUS_FILTER_EXCLUSION_PREFIX)) {
      const splitParam = routeQueryParam.split("-");
      if (splitParam.length !== 2) return;
      if (!checkRouteParam(splitParam[1])) return;

      return {
        exclude: true,
        status:
          splitParam[1].toLocaleUpperCase() as OrderOptimizerTaxiListingItemStatus,
      };
    } else {
      if (!checkRouteParam(routeQueryParam)) return;

      return {
        exclude: false,
        status:
          routeQueryParam.toLocaleUpperCase() as OrderOptimizerTaxiListingItemStatus,
      };
    }
  };

  return {
    getFilterDefinition,
    getStatusRouteQueryParamFromValue,
    getStatusValueFromRouteQueryParam,
  };
};

export default orderOptimizerTaxiListingFiltersHelper;
